import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Github, Linkedin, FileText, Menu, X, Briefcase, Eye, Clock } from 'lucide-react';
import Typewriter from 'typewriter-effect';
import About from './About';
import Contact from './Contact';
import Portfolio from './Portfolio';
import { personalInfo } from '../data/personalInfo';

const VisitCounter = () => {
  const [visits, setVisits] = React.useState(0);

  React.useEffect(() => {
    // Get current visit count from localStorage
    const currentVisits = parseInt(localStorage.getItem('siteVisits') || '0');

    // Increment visits
    const newVisits = currentVisits + 1;
    localStorage.setItem('siteVisits', newVisits.toString());
    setVisits(newVisits);
  }, []);

  return (
    <span style={{
      fontSize: '1.125rem',
      color: '#60a5fa',
      fontWeight: 'bold'
    }}>
      {visits.toLocaleString()}+
    </span>
  );
};

const AllTimeCodingStats = () => {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '8px'
    }}>
      <img
        src="https://wakatime.com/badge/user/1a29077c-352e-4138-ae85-58f8b053a010.svg"
        alt="Total coding time"
        style={{
          height: 'auto',
          maxWidth: '100%',
          borderRadius: '4px',
          background: 'transparent'
        }}
      />
      <span style={{
        fontSize: '0.875rem',
        color: '#d1d5db',
        textAlign: 'center'
      }}>
        All-time coding stats
      </span>
    </div>
  );
};

const Home = ({ value: { lines, setLines, darkMode } }) => {
  const [filter, setFilter] = useState('all');
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);

  const handleFilter = (newFilter) => {
    setFilter(newFilter);
  };

  // Handle scroll to hide indicator
  React.useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setShowScrollIndicator(false);
      } else if (window.scrollY < 50) {
        setShowScrollIndicator(true);
      }
    };

    // Show indicator after initial delay
    const timer = setTimeout(() => {
      setShowScrollIndicator(true);
    }, 3000);

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, []);

  const socialLinks = [
    {
      icon: Github,
      label: "GitHub",
      url: personalInfo.social.github,
      color: "hover:text-gray-900 dark:hover:text-white"
    },
    {
      icon: Linkedin,
      label: "LinkedIn",
      url: personalInfo.social.linkedin,
      color: "hover:text-blue-600"
    },
    {
      icon: Briefcase,
      label: "Upwork",
      url: personalInfo.social.upwork,
      color: "hover:text-green-600"
    },
    {
      icon: FileText,
      label: "Resume",
      url: personalInfo.social.resume,
      color: "hover:text-purple-600"
    }
  ];

  return (
    <div style={{
      flex: 1,
      transition: 'all 0.3s ease',
      marginLeft: (!lines && window.innerWidth >= 1024) ? '320px' : '0',
      minHeight: '100vh',
      background: darkMode ? '#0f172a' : '#ffffff'
    }}>
      {/* Mobile Menu Button */}
      <motion.button
        onClick={setLines}
        style={{
          position: 'fixed',
          top: '24px',
          left: '24px',
          zIndex: 50,
          display: window.innerWidth >= 1024 ? 'none' : 'block',
          padding: '8px',
          borderRadius: '8px',
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          color: 'white',
          cursor: 'pointer'
        }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {lines ? <X size={24} /> : <Menu size={24} />}
      </motion.button>

      {/* Hero Section */}
      <section id="hero" style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Animation */}
        <div style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.2
        }}>
          <div style={{
            position: 'absolute',
            top: '25%',
            left: '25%',
            width: '384px',
            height: '384px',
            background: '#0ea5e9',
            borderRadius: '50%',
            mixBlendMode: 'multiply',
            filter: 'blur(40px)',
            animation: 'pulse 3s infinite'
          }}></div>
          <div style={{
            position: 'absolute',
            top: '75%',
            right: '25%',
            width: '384px',
            height: '384px',
            background: '#a855f7',
            borderRadius: '50%',
            mixBlendMode: 'multiply',
            filter: 'blur(40px)',
            animation: 'pulse 3s infinite',
            animationDelay: '2s'
          }}></div>
        </div>

        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: window.innerWidth >= 1024 ? '0 48px' : window.innerWidth >= 768 ? '0 32px' : '0 16px',
          position: 'relative',
          zIndex: 10,
          width: '100%'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: window.innerWidth >= 900 ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: window.innerWidth >= 1024 ? '48px' : window.innerWidth >= 900 ? '32px' : '32px'
          }}>
            {/* Left Content */}
            <motion.div
              style={{
                flex: 1,
                textAlign: window.innerWidth >= 900 ? 'left' : 'center',
                width: '100%',
                minWidth: 0 // Prevents flex item from overflowing
              }}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                style={{
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  justifyContent: window.innerWidth >= 900 ? 'flex-start' : 'center'
                }}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                {/* Name */}
                <motion.h1
                  style={{
                    fontSize: window.innerWidth >= 1024 ? '4.5rem' : window.innerWidth >= 900 ? '3.75rem' : window.innerWidth >= 768 ? '3rem' : '2.25rem',
                    fontWeight: 'bold',
                    color: 'white',
                    lineHeight: 1.1
                  }}
                >
                  <span style={{ display: 'block' }}>{personalInfo.name.split(' ')[0]}</span>
                  <span className="gradient-text">{personalInfo.name.split(' ').slice(1).join(' ')}</span>
                </motion.h1>
              </motion.div>

              <motion.div
                style={{
                  fontSize: window.innerWidth >= 1024 ? '1.875rem' : window.innerWidth >= 900 ? '1.5rem' : window.innerWidth >= 768 ? '1.25rem' : '1.125rem',
                  color: '#d1d5db',
                  marginBottom: '32px',
                  height: window.innerWidth >= 768 ? '80px' : '60px'
                }}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <span style={{ color: 'white' }}>I'm </span>
                <span style={{ color: '#60a5fa', fontWeight: '600' }}>
                  <Typewriter
                    options={{
                      strings: personalInfo.typingTexts,
                      autoStart: true,
                      loop: true,
                      delay: 100,
                      deleteSpeed: 50,
                      cursor: '|'
                    }}
                  />
                </span>
              </motion.div>

              <motion.p
                style={{
                  fontSize: window.innerWidth >= 900 ? '1.125rem' : '1rem',
                  color: '#9ca3af',
                  marginBottom: '32px',
                  maxWidth: window.innerWidth >= 900 ? '512px' : '100%',
                  lineHeight: 1.6,
                  margin: window.innerWidth >= 900 ? '0 0 32px 0' : '0 auto 32px auto'
                }}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                {personalInfo.subtitle} with {personalInfo.experience.years}+ years of experience building scalable applications.
              </motion.p>

              <motion.div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: window.innerWidth >= 900 ? '16px' : '12px',
                  justifyContent: window.innerWidth >= 900 ? 'flex-start' : 'center'
                }}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                {socialLinks.map((link, index) => {
                  const IconComponent = link.icon;
                  return (
                      <motion.a
                      key={link.label}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: window.innerWidth >= 900 ? '12px' : '8px',
                        padding: window.innerWidth >= 900 ? '12px 24px' : window.innerWidth >= 768 ? '10px 18px' : '8px 16px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        color: 'white',
                        textDecoration: 'none',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        fontSize: window.innerWidth >= 900 ? '1rem' : '0.875rem',
                        whiteSpace: 'nowrap'
                      }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                    >
                      <IconComponent size={window.innerWidth >= 900 ? 20 : 18} />
                      <span style={{ fontWeight: '500' }}>{link.label}</span>
                    </motion.a>
                  );
                })}
              </motion.div>
            </motion.div>

            {/* Right Content - Stats */}
            <motion.div
              style={{
                flex: window.innerWidth >= 900 ? '0 0 auto' : '1',
                width: '100%',
                maxWidth: window.innerWidth >= 1200 ? '520px' : window.innerWidth >= 1024 ? '450px' : window.innerWidth >= 900 ? '400px' : '100%',
                minWidth: 0 // Prevents flex item from overflowing
              }}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div style={{
                display: 'grid',
                gridTemplateColumns: window.innerWidth >= 1024 ? 'repeat(3, 1fr)' : window.innerWidth >= 900 ? 'repeat(2, 1fr)' : window.innerWidth >= 480 ? 'repeat(2, 1fr)' : '1fr',
                gap: window.innerWidth >= 1024 ? '20px' : window.innerWidth >= 900 ? '16px' : window.innerWidth >= 768 ? '16px' : '12px',
                width: '100%'
              }}>
                {[
                  { label: "Years Experience", value: `${personalInfo.experience.years}+` },
                  { label: "Projects Completed", value: personalInfo.experience.totalProjects },
                  { label: "Companies Worked", value: personalInfo.experience.companiesWorked },
                  { label: "Technologies", value: personalInfo.experience.technologies },
                  {
                    label: "Site Visits",
                    value: <VisitCounter />,
                    icon: <Eye size={window.innerWidth >= 900 ? 20 : 18} style={{ color: '#60a5fa' }} />
                  },
                  {
                    label: "",
                    value: <AllTimeCodingStats />,
                    icon: <Clock size={window.innerWidth >= 900 ? 20 : 18} style={{ color: '#60a5fa' }} />
                  }
                ].map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    style={{
                      textAlign: 'center',
                      padding: window.innerWidth >= 1024 ? '20px' : window.innerWidth >= 900 ? '16px' : window.innerWidth >= 768 ? '16px 12px' : '14px 10px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(12px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px',
                      minHeight: 'auto'
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                    whileHover={{ scale: 1.05 }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      marginBottom: '8px'
                    }}>
                      {stat.icon && stat.icon}
                      <div style={{
                        fontSize: window.innerWidth >= 1024 ? '1.875rem' : window.innerWidth >= 900 ? '1.5rem' : window.innerWidth >= 768 ? '1.25rem' : '1.125rem',
                        fontWeight: 'bold',
                        color: '#60a5fa'
                      }}>
                        {stat.value}
                      </div>
                    </div>
                    <div style={{
                      fontSize: window.innerWidth >= 900 ? '0.875rem' : '0.75rem',
                      color: '#d1d5db',
                      lineHeight: 1.2
                    }}>
                      {stat.label}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Scroll Indicator */}
      {showScrollIndicator && (
        <motion.div
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            textAlign: 'center',
            pointerEvents: 'none'
          }}
          initial={{ opacity: 0 }}
          animate={{
            opacity: [0, 1, 0], // Fade in and out
            y: [0, -8, 0] // Gentle bouncing
          }}
                  transition={{
          duration: 6,
          repeat: Infinity,
          repeatDelay: 2,
          ease: "easeInOut",
          opacity: {
            duration: 4,
            repeat: Infinity,
            repeatDelay: 3
          }
        }}
          exit={{ opacity: 0, y: 20 }}
        >
        <div style={{
          background: darkMode ? 'rgba(96, 165, 250, 0.1)' : 'rgba(96, 165, 250, 0.05)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(96, 165, 250, 0.3)',
          borderRadius: '20px',
          padding: window.innerWidth < 768 ? '6px 12px' : '8px 16px',
          color: '#60a5fa',
          fontSize: window.innerWidth < 768 ? '0.75rem' : '0.875rem',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          gap: window.innerWidth < 768 ? '6px' : '8px',
          minWidth: window.innerWidth < 768 ? '140px' : 'auto'
        }}>
          <span>↓</span>
          <span>Scroll to explore</span>
          <span>↓</span>
        </div>
        </motion.div>
      )}

      {/* Other Sections */}
      <About />
      <Portfolio value={{ filter, handleFilter }} />
      <Contact />
    </div>
  );
};

export default Home;